/* eslint-disable react/destructuring-assignment */
import { useTranslation } from 'react-i18next';
import { useRequestCertificate } from '@steelbuy/data-access';
import { useListingBuyerEntityData } from '@steelbuy/data-provider';
import { DownloadFile, UploadFile, Nullable } from '@steelbuy/ts-shared';

import {
    ButtonTertiaryOnLightM,
    Card,
    CardContentAttributes,
    CardContentAttributesItem,
    CardContentProductViewDescription,
    CardContentTestCertificate,
    CardLayout,
    LoadingStatus,
    toaster,
} from '@steelbuy/ui-primitive';
import { FileMultiUploadItemView } from '../file-multi-upload-item-view/FileMultiUploadItemView';

type ListingDetailsQualityProps =
    | {
          prime: true;
          testCertificate: Array<DownloadFile | UploadFile> | null;
          description: Nullable<string> | undefined;
          apiBaseUrl: string;
          listingId?: string;
          hasRequestedCertificate?: boolean;
          showRequestCertificateCta?: boolean;
      }
    | {
          prime: false;
          description: Nullable<string>;
          picture?: Array<DownloadFile | UploadFile> | null;
          testCertificate?: Array<DownloadFile | UploadFile> | null;
          apiBaseUrl: string;
          listingId?: string;
          hasRequestedCertificate?: never;
          showRequestCertificateCta?: never;
      };

export const ListingDetailsQuality = (props: ListingDetailsQualityProps) => {
    const {
        prime,
        testCertificate,
        apiBaseUrl,
        listingId,
        hasRequestedCertificate = false,
        showRequestCertificateCta = false,
    } = props;
    const { t } = useTranslation(['translation', 'uiDomain']);
    const listingEntityData = useListingBuyerEntityData();
    const { mutate, isLoading } = useRequestCertificate();

    const handleRequestCertificate = () => {
        if (listingId) {
            mutate(listingId, {
                onSuccess: () => {
                    listingEntityData.resolveFetchStatus();
                    toaster.success(t('uiDomain:materialTestCert.requestCertificateSuccess'));
                },
                onError: () => {
                    toaster.error(t('uiDomain:materialTestCert.requestCertificateError'));
                },
            });
        }
    };

    return (
        <>
            {!!testCertificate?.length && (
                <Card isClickable={false}>
                    <CardLayout>
                        <CardContentTestCertificate header={t('application.listingDetails.testCertificateTitle')}>
                            {testCertificate !== null ? (
                                <FileMultiUploadItemView files={testCertificate} apiBaseUrl={apiBaseUrl} />
                            ) : (
                                '-'
                            )}
                        </CardContentTestCertificate>
                    </CardLayout>
                </Card>
            )}
            {prime && !testCertificate?.length && (
                <div className="test-cert-to-follow">
                    <Card isClickable={false}>
                        <CardLayout>
                            <CardContentAttributes
                                header={t('application.listingDetails.testCertificateTitle')}
                                hasGrid={false}
                            >
                                <CardContentAttributesItem value={t('uiDomain:materialTestCert.testCertToFollow')}>
                                    {listingId && showRequestCertificateCta && (
                                        <ButtonTertiaryOnLightM
                                            label={t('uiDomain:materialTestCert.requestCertificate')}
                                            onClick={handleRequestCertificate}
                                            loadingStatus={isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                                            disabled={hasRequestedCertificate}
                                        />
                                    )}
                                </CardContentAttributesItem>
                            </CardContentAttributes>
                        </CardLayout>
                    </Card>
                </div>
            )}
            {(props.description || prime === false) && (
                <Card isClickable={false}>
                    <CardLayout>
                        <CardContentProductViewDescription
                            header={t('application.listingDetails.detailedDescription')}
                            descriptionText={props.description ?? '-'}
                        >
                            {prime === false && props.picture?.length ? (
                                <FileMultiUploadItemView files={props.picture} apiBaseUrl={apiBaseUrl} isPicture />
                            ) : null}
                        </CardContentProductViewDescription>
                    </CardLayout>
                </Card>
            )}
        </>
    );
};
